import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const FeaturesSection = ({ data }) => {
  if (!data) return null;

  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        pb-10
      "
    >
      {/* Background Glow */}

      <div
        className="
          absolute
          -left-32
          top-20
          h-[320px]
          w-[320px]
          rounded-full
          bg-orange-500/10
          blur-[180px]
        "
      />

      <div
        className="
          absolute
          right-0
          bottom-0
          h-[320px]
          w-[320px]
          rounded-full
          bg-orange-500/10
          blur-[180px]
        "
      />

      <div
        className="
          relative
          mx-auto
          max-w-7xl
          px-5
          sm:px-6
          lg:px-8
        "
      >
        {/* Heading */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
          }}
          viewport={{
            once: true,
          }}
          className="
            mx-auto
            max-w-3xl
            text-center
          "
        >
          <span
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/10
              px-5
              py-2
              text-xs
              font-semibold
              uppercase
              tracking-[3px]
              text-orange-400
            "
          >
            Why Choose This Service
          </span>

          <h2
            className="
              mt-5
              text-3xl
              font-bold
              leading-tight
              theme-text-primary
              sm:text-4xl
              lg:text-5xl
            "
          >
            {data.title}
          </h2>

          <p
            className="
              mx-auto
              mt-5
              max-w-2xl
              text-base
              leading-7
              theme-text-muted
              sm:text-lg
            "
          >
            Everything you need for a smooth, secure and hassle-free
            registration process.
          </p>
        </motion.div>

        {/* Features Grid */}

        <div
          className="
            mt-14
            grid
            grid-cols-1
            gap-5
            sm:gap-6
            md:grid-cols-2
            xl:grid-cols-3
          "
        >
          {data.items.map((feature, index) => (
            <motion.div
              key={index}
              initial={{
                opacity: 0,
                y: 35,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.45,
                delay: index * 0.08,
              }}
              whileHover={{
                y: -6,
              }}
              className="
                group
                relative
                overflow-hidden
                rounded-3xl
                border
                border-[var(--border-primary)]
                bg-[var(--card-bg-secondary)]
                p-6
                sm:p-7
                transition-all
                duration-300
                hover:border-orange-500/30
                hover:bg-[var(--card-bg)]
                hover:shadow-[0_16px_45px_rgba(255,140,0,0.08)]
              "
            >
              {/* Soft Background Glow */}

              <div
                className="
                  absolute
                  -right-16
                  -top-16
                  h-44
                  w-44
                  rounded-full
                  bg-orange-500/5
                  blur-3xl
                  opacity-0
                  transition
                  duration-500
                  group-hover:opacity-100
                "
              />

              {/* Header */}

              <div
                className="
                  relative
                  z-10
                  flex
                  items-start
                  justify-between
                "
              >
                {/* Icon */}

                <div
                  className="
                    flex
                    h-12
                    w-12
                    items-center
                    justify-center
                    rounded-2xl
                    border
                    border-orange-500/20
                    bg-orange-500/10
                    transition
                    duration-300
                    group-hover:bg-orange-500
                    sm:h-14
                    sm:w-14
                  "
                >
                  <CheckCircle2
                    size={24}
                    className="
                      text-orange-400
                      transition
                      duration-300
                      group-hover:text-white
                    "
                  />
                </div>

                {/* Number */}

                <span
                  className="
                    rounded-full
                    border
                    border-[var(--border-primary)]
                    bg-[var(--bg-tertiary)]
                    px-2.5
                    py-1
                    text-[11px]
                    font-semibold
                    tracking-[2px]
                    theme-text-muted
                    transition
                    duration-300
                    group-hover:border-orange-500/30
                    group-hover:text-orange-400
                    sm:px-3
                  "
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Title */}

              <h3
                className="
                  relative
                  z-10
                  mt-6
                  text-xl
                  font-semibold
                  leading-7
                  theme-text-primary
                  transition-colors
                  duration-300
                  group-hover:text-orange-300
                  sm:text-2xl
                "
              >
                {feature.title}
              </h3>

              {/* Description */}

              <p
                className="
                  relative
                  z-10
                  mt-4
                  text-[15px]
                  leading-7
                  theme-text-muted
                "
              >
                {feature.description}
              </p>

              {/* Bottom Section */}

              <div
                className="
                  relative
                  z-10
                  mt-6
                  flex
                  items-center
                  justify-between
                "
              >
                <div
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >
                  <div
                    className="
                      h-2
                      w-2
                      rounded-full
                      bg-orange-500
                    "
                  />

                  <span
                    className="
                      text-xs
                      font-medium
                      theme-text-secondary
                      sm:text-sm
                    "
                  >
                    Trusted Solution
                  </span>
                </div>

                {/* Small Hover Arrow */}

                <motion.div
                  initial={{
                    x: 0,
                  }}
                  whileHover={{
                    x: 4,
                  }}
                  className="
                    text-orange-400
                    opacity-0
                    transition
                    duration-300
                    group-hover:opacity-100
                  "
                >
                  →
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;